import React, { useEffect, useState } from 'react';
import portalService from '../../services/portalService';
import { useLanguage, t } from '../../context/LanguageContext';

function currentPeriodBounds() {
  const now = new Date();
  const day = now.getDate();
  const y = now.getFullYear(), m = now.getMonth();
  const from = day <= 15 ? new Date(y, m, 1) : new Date(y, m, 16);
  const to   = day <= 15 ? new Date(y, m, 15) : new Date(y, m + 1, 0);
  return { from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] };
}

function MaskedValue({ value, prefix = '' }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className="font-monospace" style={{ fontSize: 18, fontWeight: 700 }}>
        {visible ? `${prefix}${value}` : '●●●●●'}
      </span>
      <button
        className="btn btn-link btn-sm p-0 text-muted"
        style={{ fontSize: 16, lineHeight: 1 }}
        onClick={() => setVisible(v => !v)}
      >
        {visible ? '🙈' : '👁'}
      </button>
    </span>
  );
}

export default function PortalPayPage() {
  const { lang } = useLanguage();
  const [bounds, setBounds] = useState(currentPeriodBounds());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    portalService.getPay(bounds.from, bounds.to)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [bounds]);

  const goBack = () => {
    const f = new Date(bounds.from);
    f.setDate(f.getDate() - 1);
    const prevTo = f.toISOString().split('T')[0];
    const prevFrom = f.getDate() <= 15
      ? new Date(f.getFullYear(), f.getMonth(), 1).toISOString().split('T')[0]
      : new Date(f.getFullYear(), f.getMonth(), 16).toISOString().split('T')[0];
    setBounds({ from: prevFrom, to: prevTo });
  };
  const goForward = () => {
    const next = new Date(bounds.to); next.setDate(next.getDate() + 1);
    const y = next.getFullYear(), m = next.getMonth(), d = next.getDate();
    const from = d <= 15 ? new Date(y, m, 1) : new Date(y, m, 16);
    const to   = d <= 15 ? new Date(y, m, 15) : new Date(y, m + 1, 0);
    setBounds({ from: from.toISOString().split('T')[0], to: to.toISOString().split('T')[0] });
  };

  return (
    <div>
      <h5 className="fw-bold mb-3">{t('My Pay', 'Mi pago', lang)}</h5>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={goBack}>&larr;</button>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{bounds.from} — {bounds.to}</span>
        <button className="btn btn-outline-secondary btn-sm" onClick={goForward}>&rarr;</button>
      </div>

      {loading ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary" /></div>
      ) : !data ? (
        <div className="text-muted text-center py-4" style={{ fontSize: 13 }}>{t('Could not load pay data.', 'No se pudieron cargar los datos de pago.', lang)}</div>
      ) : (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <div className="text-muted mb-1" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Total paid hours this period', 'Horas pagadas este período', lang)}</div>
              <div className="fw-bold" style={{ fontSize: 28 }}>{data.totalNetHours} h</div>
              {(data.ptoHours > 0 || data.sickHours > 0) && (
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  {data.workedHours} h {t('worked', 'trabajadas', lang)}
                  {data.ptoHours > 0 && ` + ${data.ptoHours} h PTO`}
                  {data.sickHours > 0 && ` + ${data.sickHours} h ${t('Sick', 'Enfermedad', lang)}`}
                </div>
              )}
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="text-muted mb-1" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {data.payType === 'hourly' ? t('Hourly Rate', 'Tarifa por hora', lang) : t('Salary', 'Salario', lang)}
              </div>
              {data.payType === 'hourly' && data.hourlyRate != null && (
                <MaskedValue value={`$${Number(data.hourlyRate).toFixed(2)}/hr`} />
              )}
              {data.payType === 'salary' && data.annualSalary != null && (
                <MaskedValue value={`$${Number(data.annualSalary).toLocaleString()}/yr`} />
              )}
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="text-muted mb-1" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('Estimated Gross Pay', 'Pago bruto estimado', lang)}</div>
              {data.grossPay != null
                ? <MaskedValue value={`$${Number(data.grossPay).toFixed(2)}`} />
                : <span className="text-muted" style={{ fontSize: 13 }}>{t('Not available', 'No disponible', lang)}</span>}
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                {t('Estimated only. Actual pay processed externally.', 'Solo estimado. El pago real se procesa externamente.', lang)}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
